import React, { useState } from "react";
import { Dialog, LoadingSkeleton, Toggle } from "@narmi/design_system";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useNotificationContext } from "cerulean";
import HistoryTable from "./HistoryTable";
import useUserConfiguration from "./hooks/useUserConfiguration";
import useInstitutionSetting from "./hooks/useInstitutionSetting";
import styles from "./CustomSettingPage.module.css";

const SETTING_NAME = "LENDING_APPLICATION_STEPS";

type StepOption = {
  key: string;
  label: string;
  description: string;
};

type StepsValue = {
  [key: string]: boolean;
};

const STEP_OPTIONS: StepOption[] = [
  {
    key: "living_arrangement",
    label: "Show living arrangement",
    description: "Show the living arrangement step in all lending application flows.",
  },
];

const previewStyles = {
  variables: {
    light: {
      addedBackground: "#fff",
      removedBackground: "#fff",
      addedGutterBackground: "#fff",
      removedGutterBackground: "#f7f7f7",
      wordAddedBackground: "#37B374",
      wordRemovedBackground: "#D93B3B",
      emptyLineBackground: "#D93B3B",
    },
  },
};

const DEFAULT_VALUE: StepsValue = {
  living_arrangement: true,
};

const labelForStepKey = (key: string) => STEP_OPTIONS.find((s) => s.key === key)?.label || key;

function LendingApplicationSteps() {
  const { setting, save } = useInstitutionSetting(SETTING_NAME);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const [isHistoryDisplayed, setIsHistoryDisplayed] = useState(true);
  const [oldPreviewValue, setOldPreviewValue] = React.useState("");
  const [newPreviewValue, setNewPreviewValue] = React.useState("");
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const { currentUser, permissions } = useUserConfiguration();
  const { sendNotification } = useNotificationContext();
  const canEditSettings = permissions.includes("backoffice.change_settings");

  const settingValue: StepsValue | null = setting
    ? { ...DEFAULT_VALUE, ...JSON.parse(setting.value) }
    : null;

  const updateValue = async (key: string, value: boolean) => {
    setPendingKey(key);
    try {
      await save(
        JSON.stringify({ ...settingValue, [key]: value }),
        `[AUTOMATED] ${currentUser.email} updated ${key} to ${value}`
      );
      setHistoryVersion((version) => version + 1);
      sendNotification({
        type: "success",
        text: `${labelForStepKey(key)} ${value ? "enabled" : "disabled"}.`,
      });
    } catch (e) {
      sendNotification({
        type: "negative",
        text: e instanceof Error ? e.message : "Failed to save setting.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  const setOldAndNewPreviewValues = (oldValue: string, newValue: string) => {
    setOldPreviewValue(oldValue);
    setNewPreviewValue(newValue);
    setPreviewIsOpen(true);
  };

  return (
    <div className={`${styles.page} nds-typography`}>
      <div className="margin--bottom--m" style={{ display: "flex", alignItems: "center" }}>
        <h2 style={{ marginTop: "0.5rem" }}>Lending application step visibility</h2>
      </div>
      <div className="padding--all--m">
        {!settingValue ? (
          <LoadingSkeleton isLoading lines={STEP_OPTIONS.length} />
        ) : (
          STEP_OPTIONS.map((step) => (
            <div key={step.key}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <div>
                  <div className="margin--bottom--xxs fontWeight--semibold">{step.label}</div>
                  <div className="margin--bottom--s" style={{ color: "var(--color-mediumGrey)" }}>
                    {step.description}
                  </div>
                </div>
                <Toggle
                  labelledBy={step.key}
                  onChange={(next) => {
                    updateValue(step.key, next);
                  }}
                  isActive={settingValue[step.key]}
                  isLoading={pendingKey === step.key}
                  disabled={(pendingKey !== null && pendingKey !== step.key) || !canEditSettings}
                />
              </div>
              <hr className="margin--top--m" style={{ marginLeft: 0, marginRight: 0 }} />
            </div>
          ))
        )}
        <div className="margin--top--xl">
          <h3>Change log</h3>
          {isHistoryDisplayed && (
            <HistoryTable
              key={historyVersion}
              settingName={SETTING_NAME}
              setOldAndNewPreviewValues={setOldAndNewPreviewValues}
              sendNotification={sendNotification}
              setIsHistoryDisplayed={setIsHistoryDisplayed}
            />
          )}
        </div>
        <Dialog
          isOpen={previewIsOpen}
          title="Preview"
          onUserDismiss={() => setPreviewIsOpen(false)}
          width="100%"
        >
          <ReactDiffViewer
            oldValue={oldPreviewValue}
            newValue={newPreviewValue}
            splitView
            showDiffOnly={false}
            styles={previewStyles}
            compareMethod={DiffMethod.WORDS}
            leftTitle="Current value"
            rightTitle="New value"
          />
        </Dialog>
      </div>
    </div>
  );
}

export default LendingApplicationSteps;
